import { AlertTriangle, Inbox, LoaderCircle, ShieldAlert } from 'lucide-react';

import { ApiClientError } from '../api/client.js';
import { useUiPreferences } from '../i18n/ui-preferences.js';

export function LoadingState({ label }: { label?: string }) {
  const { t } = useUiPreferences();
  const resolvedLabel = label ?? t('正在加载', 'Loading');
  return (
    <div className="page-state" aria-busy="true" aria-label={resolvedLabel}>
      <LoaderCircle className="spin" aria-hidden="true" />
      <p>{resolvedLabel}</p>
    </div>
  );
}

export function ErrorState({ error, retry }: { error: unknown; retry?(): void }) {
  const { t } = useUiPreferences();
  const clientError = error instanceof ApiClientError ? error : null;
  const sessionExpired = clientError?.status === 401;
  return (
    <div className="page-state error" role="alert">
      <AlertTriangle aria-hidden="true" />
      <h2>
        {sessionExpired
          ? t('访问会话已失效', 'Access session expired')
          : t('加载失败', 'Unable to load')}
      </h2>
      <p>
        {sessionExpired
          ? t('请重新使用公司飞书账号登录。', 'Sign in with your company Feishu account again.')
          : (clientError?.message ??
            t('TCode 暂时无法完成该请求。', 'TCode could not complete this request.'))}
      </p>
      {clientError?.requestId ? (
        <small className="mono">Request ID: {clientError.requestId}</small>
      ) : null}
      {sessionExpired ? (
        <a className="button secondary" href={reauthorizeUrl()}>
          {t('重新登录', 'Sign in again')}
        </a>
      ) : retry ? (
        <button type="button" className="button secondary" onClick={retry}>
          {t('重试', 'Retry')}
        </button>
      ) : null}
    </div>
  );
}

export function WorkItemAccessDeniedState({ onReturn }: { onReturn(): void }) {
  const { t } = useUiPreferences();
  return (
    <div className="page-state error" role="alert">
      <ShieldAlert aria-hidden="true" />
      <h2>{t('无权限访问', 'Access denied')}</h2>
      <p>
        {t(
          '这个 Work Item 由其他用户创建，你无法查看其内容。',
          'This Work Item was created by another user, so you cannot view its content.',
        )}
      </p>
      <button type="button" className="button secondary" onClick={onReturn}>
        {t('返回工作项首页', 'Return to Work Items')}
      </button>
    </div>
  );
}

function reauthorizeUrl(): string {
  const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  return `/api/v1/auth/feishu?${new URLSearchParams({ returnTo }).toString()}`;
}

export function EmptyState({ title, description }: { title: string; description: string }) {
  return (
    <div className="page-state">
      <Inbox aria-hidden="true" />
      <h2>{title}</h2>
      <p>{description}</p>
    </div>
  );
}
